import { Account, DimensionUnit, FreightService, QuoteStatus, RequireOnly, WeightUnit } from '@pelicargo/types';
import { z } from 'zod';

import { EmailTemplateName } from '../../../api/src/lib/codegen/email';
import { EmailOpenerTemplates, greetingMessages } from './constants';
import { Quote, Request } from './types';

/**
 * Matches loosely the Contact model in the API
 */
export type AccountIdAndName = RequireOnly<Account, 'id' | 'name'>;

export type ContactValue = {
  id?: number;
  created_at?: Date;
  updated_at?: Date;
  full_name: string;
  preferred_name?: string;
  email: string;
  accounts?: AccountIdAndName[];
  providers?: string[];
  airports?: string[];
  phone?: string;
  tags?: string[];
  existsInSystem?: boolean;
};

export type AirlineFormValue = {
  id: string;
  quoteId: number;
  isExpanded?: boolean;
  originAirportId?: string;
  recipients: ContactValue[];
};
export type TruckingFormValue = {
  id: string;
  quoteId: number;
  isExpanded?: boolean;
  originAirportId?: string;
  recipients: ContactValue[];
};
export type WarehouseFormValue = {
  id: string;
  quoteId: number;
  isExpanded?: boolean;
  originAirportId?: string;
  recipients: ContactValue[];
};

export const recipientValidationSchema = z.object({
  id: z.coerce.number(),
  preferred_name: z.string(),
  email: z.string().email(),
});

export const airlineValidationSchema = z.object({
  id: z.string(),
  quoteId: z.coerce.number(),
  isExpanded: z.boolean(),
  originAirportId: z.string().optional(),
  recipients: z.array(recipientValidationSchema).optional(),
});
export const truckingValidationSchema = z.object({
  id: z.string(),
  quoteId: z.coerce.number(),
  isExpanded: z.boolean(),
  originAirportId: z.string().optional(),
  recipients: z.array(recipientValidationSchema).optional(),
});
export const warehouseValidationSchema = z.object({
  id: z.string(),
  quoteId: z.coerce.number(),
  isExpanded: z.boolean(),
  recipients: z.array(recipientValidationSchema).optional(),
});

export type SharedEmailOverrides = {
  origin: string;
  destination: string;

  weightUnit: WeightUnit;
  dimensionUnit: DimensionUnit;
  senderContactId: number;

  customMessage?: string;

  clipboard?: boolean;
};

export const SharedEmailValidationSchema = {
  senderContactId: z.coerce.number().min(1, 'You must specify a sender contact'),
  origin: z.string(),
  destination: z.string(),
  weightUnit: z.nativeEnum(WeightUnit),
  dimensionUnit: z.nativeEnum(DimensionUnit),

  customMessage: z.string().optional(),

  clipboard: z.boolean().optional(),
  contentOnly: z.boolean().optional().default(false),
};

export type SharedAirlineFormValues = SharedEmailOverrides & {
  providers: AirlineFormValue[];
};

export const SharedAirlineValidationSchema = {
  ...SharedEmailValidationSchema,
  providers: z.array(airlineValidationSchema).min(1, { message: 'You must add at least one airline' }),
};

export const getDefaultAirlinesFormFromQuote = (quote: Quote): AirlineFormValue => ({
  id: quote?.provider_id,
  quoteId: quote?.id,
  originAirportId: quote?.origin_airport_id,
  isExpanded: false,
  recipients: [],
});
export const getDefaultTruckingsFormFromQuote = (quote: Quote): TruckingFormValue => ({
  id: quote?.provider_id,
  quoteId: quote?.id,
  originAirportId: quote?.origin_airport_id,
  isExpanded: false,
  recipients: [],
});
export const getDefaultWarehousesFormFromQuote = (quote: Quote): WarehouseFormValue => ({
  id: quote?.provider_id,
  quoteId: quote?.id,
  originAirportId: quote?.origin_airport_id,
  isExpanded: false,
  recipients: [],
});

export const ensureQuoteStatusIs = (statuses: QuoteStatus[]) => (quote: Quote) =>
  statuses?.includes(quote?.status as QuoteStatus);
export const ensureFreightServiceIs = (freightServices: FreightService[]) => (quote: Quote) =>
  freightServices.includes(quote?.freight_service);

export const getSharedAirlineDefaultsFromRequest = (
  request: Request & {
    booked_quote_variant: {
      quote: {
        origin_airport_id: string;
        destination_airport_id: string;
      };
    };
    origin_airports: { id: string }[];
    destination_airports: { id: string }[];
  },
): SharedEmailOverrides => ({
  origin: request?.booked_quote_variant?.quote?.origin_airport_id ?? request?.origin_airports?.[0]?.id,
  destination:
    request?.booked_quote_variant?.quote?.destination_airport_id ??
    request?.destination_airports?.[request?.destination_airports?.length - 1]?.id,
  weightUnit: WeightUnit.kg,
  dimensionUnit: DimensionUnit.in,
  senderContactId: request?.default_sender_contact_id,
});

export const getSharedInlandDefaultsFromRequest = (
  request: Request & {
    booked_quote_variant: {
      quote: {
        origin_airport_id: string;
        destination_airport_id: string;
      };
    };
    origin_airports: { id: string }[];
    destination_airports: { id: string }[];
  },
): SharedEmailOverrides => ({
  origin: request?.booked_quote_variant?.quote?.origin_airport_id ?? request?.origin_airports?.[0]?.id,
  destination:
    request?.booked_quote_variant?.quote?.destination_airport_id ??
    request?.destination_airports?.[request?.destination_airports?.length - 1]?.id,
  weightUnit: WeightUnit.lb,
  dimensionUnit: DimensionUnit.in,
  senderContactId: request?.default_sender_contact_id,
});

export const getCustomerDefaultsFromRequest = (
  request: RequireOnly<Request, 'default_sender_contact'> & { author: { contact: ContactValue } },
) => {
  const authorContact = request?.default_sender_contact;
  const bookers = request?.quotes?.map?.((x) => x?.booked_by?.contact)?.filter?.(Boolean) ?? [];
  return { recipients: [authorContact, ...bookers].filter(Boolean) };
};

export const getRandomGreetingMessage = () => {
  const randomIndex = Math.floor(Math.random() * greetingMessages.length);
  return greetingMessages[randomIndex];
};

export const getDefaultCustomMessage = (openingLine: string) => {
  const greeting = getRandomGreetingMessage();
  return `${greeting} ${openingLine}`;
};

export const getOpenerByEmailTemplate = (emailTemplate: EmailTemplateName) => {
  const greeting = getRandomGreetingMessage();
  return `Hello {{first_name}},\n\n${greeting} ${EmailOpenerTemplates[emailTemplate]}`;
};

export const getSpotBookingOpener = () => {
  const greeting = getRandomGreetingMessage();
  return `Hello {{first_name}},\n\n${greeting} {{airline_name}} has been awarded this cargo at the agreed spot rate. The booking is confirmed — please see the details below and apply the spot rate accordingly.`;
};

export const sendEmailsToValidProviders = async <Data = Record<string, any>>(
  data: Data,
  providers: (AirlineFormValue | TruckingFormValue | WarehouseFormValue)[],
  sendEmailCallback: (provider: AirlineFormValue | TruckingFormValue | WarehouseFormValue, data: Data) => Promise<any>,
): Promise<string> => {
  if (!providers?.length) throw new Error('Please add one or more providers');

  const providersToEmail = providers.filter(({ isExpanded, recipients }) => isExpanded && !!recipients?.length);
  if (!providersToEmail?.length) throw new Error('No recipients selected');

  // send one email to each provider
  await Promise.all(providersToEmail.map(async (provider) => await sendEmailCallback(provider, data)));

  const numberOfEmails = providersToEmail?.length;
  if (!data?.['clipboard']) {
    const message = `${numberOfEmails} Email${numberOfEmails > 1 ? 's' : ''} sent successfully`;

    return message;
  } else {
    return 'Email copied to clipboard';
  }
};

export const hasCustomerEmail = (contacts: Partial<ContactValue>[]) => {
  for (const contact of contacts) {
    if (!contact?.email?.endsWith('pelicargo.com')) {
      return true;
    }
  }
  return false;
};
